import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

/**
 * Shared React Query defaults, extracted from
 * packages/frontend/src/app/providers.tsx: 5 min stale time, 1 retry, no
 * refetch-on-focus outside production builds.
 *
 * @param onError Optional hook invoked for every failed query AND mutation
 *   across the app, on top of each call site's own `onError`/`isError`
 *   handling. Added for the admin app's global `ADMIN_MFA_REQUIRED` 403
 *   handling (packages/admin/src/app/providers.tsx) — that error can arrive
 *   from any admin API call, not one specific mutation, so a single
 *   per-cache hook is the only place that sees all of them. Left `undefined`
 *   (the previous, unparameterized behaviour) for every other caller.
 */
export function createQueryClient(
  onError?: (error: unknown) => void
): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: import.meta.env.PROD,
      },
    },
    ...(onError && {
      queryCache: new QueryCache({ onError }),
      mutationCache: new MutationCache({ onError }),
    }),
  });
}
